import { Field, Label, Switch } from '@headlessui/react';
import { ReactNode } from 'react';
import { classNames } from '../../utils';
import Loader from './Loader';

interface Props {
  label?: string | ReactNode;
  checked?: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  className?: string;
  size?: 'small' | 'normal';
}

export default function Toggle({
  label,
  checked,
  onChange,
  disabled,
  readOnly,
  loading,
  className,
  size = 'normal',
}: Props) {
  return (
    <Field
      as="div"
      className="flex items-center"
      aria-disabled={disabled ? 'true' : undefined}
    >
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={readOnly || loading ? undefined : onChange}
        aria-readonly={readOnly || loading ? 'true' : 'false'}
        className={classNames(
          'disabled:bg-gray-400',
          checked ? 'bg-indigo-600' : 'bg-gray-200',
          size === 'normal'
            ? 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
            : 'group relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none',
          className,
        )}
      >
        {size === 'normal' ? (
          <span
            aria-hidden="true"
            className={classNames(
              checked ? 'translate-x-5' : 'translate-x-0',
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
            )}
          />
        ) : (
          <>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute size-full rounded-md bg-white"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute mx-auto h-4 w-9 rounded-full bg-gray-200 transition-colors duration-200 ease-in-out group-data-[checked]:bg-indigo-600"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-0 inline-block size-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out group-data-[checked]:translate-x-5"
            />
          </>
        )}
      </Switch>
      <Label as="span" className="ml-3 text-sm">
        {loading ? <Loader size={4} /> : label}
      </Label>
    </Field>
  );
}
